import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice
} from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { RootState } from 'Douleutaras/redux/store';
import {
  Job, JobStatus,
  JSONApiDataObject,
  User
} from 'Douleutaras/types';
import normalize from 'json-api-normalizer';
import { categoriesSlice } from 'Douleutaras/redux/data/categories/categoriesSlice';
import { jobAttributesSlice } from 'Douleutaras/redux/data/jobAttributes/jobAttributes';
import { jobAttributeValuesSlice } from 'Douleutaras/redux/data/jobAttributeValues/jobAttributeValues';
import { usersSlice } from 'Douleutaras/redux/data/users/usersSlice';
import { jobMunicipalitySlice } from 'Douleutaras/redux/data/jobMunicipality/jobMunicipalitySlice';
import { jobBidsSlice } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import { jobBidsBookNowSlice } from 'Douleutaras/redux/data/jobBids/jobBidsBookNowSlice';
import { businessUnitsSlice } from 'Douleutaras/redux/data/businessUnits/businessUnitsSlice';
import { jobBidsStatusBuyerSlice } from 'Douleutaras/redux/data/jobBids/jobBidsStatusBuyerSlice';
import jobPriceDefiningQuestionsSlice from 'Douleutaras/redux/data/jobPriceDefiningQuestions/jobPriceDefiningQuestionsSlice';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { jobBidsFeedbackSlice } from 'Douleutaras/redux/data/jobBids/jobBidsFeedbackSlice';
import {
  JobNormalizedEntityList, JobSliceType,
  PostJobFormAttributes,
  TFetchJobs,
  TransformedFormData
} from 'Douleutaras/redux/data/jobs/types';
import defaultsDeep from 'lodash/defaultsDeep';

export const ENDPOINT_JOB_VIEW = 'job/view';
export const ENDPOINT_JOB_LIST = 'job/list';
export const ENDPOINT_JOB_CANCEL = 'job/cancel;';
export const API_JOB_ENDPOINT = 'api/public/v3/job';
export const API_TMP_JOB_ENDPOINT = 'api/public/v3/tmp_job';

export const ACTIVE_JOB_STATUSES = [
  JobStatus.ACTIVE,
  JobStatus.ON_MY_WAY,
  JobStatus.PENDING_APPROVAL,
  JobStatus.UNCONFIRMED,
  JobStatus.PENDING
];

const jobsAdapter = createEntityAdapter<JSONApiDataObject<Job>>({
  selectId: job => job.id
});

/**
 * Filter by status if passed.
 * endpointIdentifier - An additional identifier to make endpoint unique if additional data is requests.
 */
const getIdentifier = (status: Array<string> | string): string => {
  return Array.isArray(status) ? status.join(',') : status;
};

const generateAdditionalParams = (status?, page = '1', size = '10', custom = {}) => {
  const result = {
    sort: '-created',
    'page[size]': size,
    'page[number]': page,
    ...custom
  };
  // Filter by status if passed
  if (Array.isArray(status)) {
    result['filter[status.in]'] = status.join(',');
  } else if (status) {
    result['filter[status]'] = status;
  }

  return result;
};

export const transformJobAttributesToUserData =
  ({ formData, userGis}: { formData: Partial<PostJobFormAttributes>, userGis: Gis }): Partial<User> => {

  return {
    ...formData.questions,
    consents: [
      {
        type: 'customer_tos',
        text: ''
      },
      {
        type: 'marketing',
        text: ''
      }
    ],
    email: formData.email,
    fullname: formData.name,
    gis: userGis,
    phone: formData.phone
  };
};

export const postJob = createAsyncThunk(
  'jobs/post',
  async ({ formData }: { formData: TransformedFormData }, api) => {

    const data = formData;
    data.type = 'jobs';

    const response = await makeRequest(
      `${API_JOB_ENDPOINT}/`,
      { method: 'POST',
        body: {
          data
        }
      }
    )(api);

    // 409 is duplicate job
    const isDuplicate = response?.payload?.response?.status === 409;
    const ok = response?.status === 200 || response?.status === 201 || isDuplicate;

    if (!ok) {
      return {
        error: true,
        errors: response.payload?.response?.data.errors,
        responseStatus: response.payload?.response?.status
      };
    }

    if (isDuplicate) {
      // Build response payload for duplicate jobs
      return {
        isDuplicate: true,
          data: {
            attributes: { ...response.payload.response.data }
          }
      };
    }

    return response.data;
  }
);

export const postJobTmp = createAsyncThunk(
  'jobs/post_tmp',
  async ({ formData }: { formData: TransformedFormData }, api) => {
    const data = formData;
    data.type = 'tmp-job';
    const response = await makeRequest(
      `${API_TMP_JOB_ENDPOINT}/`,
      { method: 'POST',
        body: {
          data
        }
      }
    )(api);

    const ok = response?.status === 200 || response?.status === 201;

    if (!ok) {
      return {
        error: true,
        errors: response.payload?.response?.data.errors,
        responseStatus: response.payload?.response?.status
      };
    }

    return response.data;
  }
);

export const fetchJobs = createAsyncThunk(
'jobs/getAll',
async ({ includes, status, page = '1', size = '10', customFilters }: Partial<TFetchJobs> = undefined, api) => {
  const additionalGetParams = generateAdditionalParams(status, page, size, customFilters);
  const endpointIdentifier = getIdentifier(status);

  const response = await makeRequest(
    `${API_JOB_ENDPOINT}/`,
    {
      includes,
      additionalGetParams
    }
  )(api);

  return jobsHandleResponse(response, api, `${ENDPOINT_JOB_LIST}/${endpointIdentifier}/${page}`);
});

const jobsHandleResponse = (response, api, endpoint) => {
  const normalizedJobList = normalize(response.data, { endpoint });
  const { categoriesAdded } = categoriesSlice.actions;
  const { jobBidsAdded } = jobBidsSlice.actions;
  const { jobBidsBookNowAdded } = jobBidsBookNowSlice.actions;
  const { businessUnitsAdded } = businessUnitsSlice.actions;
  const { jobAttributeValuesAdded } = jobAttributeValuesSlice.actions;
  const { bidBuyerStatusAdded } = jobBidsStatusBuyerSlice.actions;
  const { jobPriceDefiningQuestionAdded } = jobPriceDefiningQuestionsSlice.actions;
  const { usersAdded } = usersSlice.actions;
  const { jobBidsFeedbackAdded } = jobBidsFeedbackSlice.actions;

  if (typeof normalizedJobList.businessUnits !== 'undefined') {
    api.dispatch(
      businessUnitsAdded(normalizedJobList.businessUnits)
    );
  }

  if (typeof normalizedJobList.bookNowEntries !== 'undefined') {
    api.dispatch(
      jobBidsBookNowAdded(normalizedJobList.bookNowEntries)
    );
  }

  if (typeof normalizedJobList.categories !== 'undefined') {
    api.dispatch(
      categoriesAdded(normalizedJobList.categories)
    );
  }
  if (typeof normalizedJobList.bids !== 'undefined') {
    api.dispatch(
      jobBidsAdded(normalizedJobList.bids)
    );
  }
  if (typeof normalizedJobList.answers !== 'undefined') {
    api.dispatch(
      jobAttributeValuesAdded(normalizedJobList.answers)
    );
  }

  if (typeof normalizedJobList.bidStatusBuyerUpdate !== 'undefined') {
    api.dispatch(
      bidBuyerStatusAdded(normalizedJobList.bidStatusBuyerUpdate)
    );
  }

  if (typeof normalizedJobList.priceDefiningQnaChoices !== 'undefined') {
    api.dispatch(
      jobPriceDefiningQuestionAdded(normalizedJobList.priceDefiningQnaChoices)
    );
  }

  if (normalizedJobList.users) {
    api.dispatch(
      usersAdded(normalizedJobList.users)
    );
  }

  if (normalizedJobList.feedback) {
    api.dispatch(
      jobBidsFeedbackAdded(normalizedJobList.feedback)
    );
  }

  return normalizedJobList;
};

/**
 * Keep in mind that you shouldn't fetch jobs stats if we already have same page jobs list is loaded.
 * In case jobs list available statistics would available as well.
 * Since "fetchJobs" and "fetchJobsStats" use the same selector "selectJobMetaByStatus" and endpointIdentifier.
 */
export const fetchJobsStats = createAsyncThunk(
  'jobs/statistics',
  async ({ includes, status, page = '1', size = '5', customFilters }: Partial<TFetchJobs> = undefined, api) => {
    const additionalGetParams = generateAdditionalParams(status, page, size, customFilters);
    const endpointIdentifier = getIdentifier(status);
    const response = await makeRequest(
      `${API_JOB_ENDPOINT}/`,
      {
        method: 'OPTIONS',
        includes,
        additionalGetParams
      }
    )(api);

    return {
      endpoint: `${ENDPOINT_JOB_LIST}/${endpointIdentifier}/${page}`,
      meta: {
        pagination: {
          count: 0
        },
        statusCounts: response.data.data.statusCounts
      }
    };
  });

export const fetchJob = createAsyncThunk(
  'jobs/getById',
  async ({ jobId, includes }: { jobId: string, includes?: Array<string> }, api) => {
    const endpoint = `${API_JOB_ENDPOINT}/${jobId}/`;
    const response = await makeRequest(endpoint, { includes })(api);

    if (response.status === 200 || response.status === 201) {
      return jobHandleResponse(response, api, ENDPOINT_JOB_VIEW);
    }

    return api.rejectWithValue({ error: response.payload });
  });

const jobHandleResponse = (response, api, endpoint) => {
  const normalizedJob = normalize(response.data, { endpoint }) as JobNormalizedEntityList;
  const { categoriesAdded } = categoriesSlice.actions;
  const { jobAttributesAdded } = jobAttributesSlice.actions;
  const { jobAttributeValuesAdded } = jobAttributeValuesSlice.actions;
  const { usersAdded } = usersSlice.actions;
  const { jobBidsAdded } = jobBidsSlice.actions;
  const { jobMunicipalityAdded } = jobMunicipalitySlice.actions;
  const { businessUnitsAdded } = businessUnitsSlice.actions;
  const { jobPriceDefiningQuestionAdded } = jobPriceDefiningQuestionsSlice.actions;
  const { jobBidsBookNowAdded } = jobBidsBookNowSlice.actions;

  if (normalizedJob.businessUnits) {
    api.dispatch(
      businessUnitsAdded(normalizedJob.businessUnits)
    );
  }

  if (normalizedJob.categories) {
    api.dispatch(
      categoriesAdded(normalizedJob.categories)
    );
  }

  if (normalizedJob.questions) {
    api.dispatch(
      jobAttributesAdded(normalizedJob.questions)
    );
  }

  if (normalizedJob.answers) {
    api.dispatch(
      jobAttributeValuesAdded(normalizedJob.answers)
    );
  }

  if (normalizedJob.municipality) {
    api.dispatch(
      jobMunicipalityAdded(normalizedJob.municipality)
    );
  }

  if (normalizedJob.users) {
    api.dispatch(
      usersAdded(normalizedJob.users)
    );
  }

  if (normalizedJob.categoryFormQnaOptions) {
    api.dispatch(
      jobPriceDefiningQuestionAdded(normalizedJob.categoryFormQnaOptions)
    );
  }

  if (normalizedJob.bookNowEntries) {
    api.dispatch(jobBidsBookNowAdded(normalizedJob.bookNowEntries));
  }

  if (normalizedJob.bids) {
    api.dispatch(jobBidsAdded(normalizedJob.bids));
  }

  return normalizedJob;
};

export const cancelJob = createAsyncThunk(
  'jobs/cancel',
  async ({ jobId, cancelReason, additionalData = {} }
           : { jobId: string, cancelReason: string, additionalData: { [index in string]: string } },
         api) => {
    const endpoint = `${API_JOB_ENDPOINT}/${jobId}/`;

    // additionalData
    let attributes = {
      status: 'cancelled',
      cancel_reason: cancelReason
    };

    if (Object.keys(additionalData).length > 0) {
      attributes = { ...attributes, ...additionalData };
    }

    const response = await makeRequest(
      endpoint,
      { method: 'PATCH',
        body: {
          data: {
            type: 'jobs',
            id: jobId,
            attributes
          }
        }
      }
    )(api);

    return normalize(response.data, { endpoint: ENDPOINT_JOB_CANCEL }) as JobNormalizedEntityList;
  }
);

export const modifyJob = createAsyncThunk(
  'jobs/modifyJob',
  async ({ jobId, attributes = {} }: { jobId: string, attributes: Partial<Job> }, api) => {
    const endpoint = `${API_JOB_ENDPOINT}/${jobId}/`;
    const response = await makeRequest(endpoint, {
      method: 'PATCH',
      body: {
        data: {
          type: 'jobs',
          id: jobId,
          attributes
        }
      }
    })(api);

    if (response.status === 200 || response.status === 201) {
      return normalize(response?.data, { endpoint }) as JobNormalizedEntityList;
    }

    return api.rejectWithValue({ error: response.payload });
  });

export const requestExtraBid = createAsyncThunk(
  'jobs/requestExtraBid',
  async ({ jobId, from }: { jobId: string, from?: string }, api) => {
    const endpoint = `${API_JOB_ENDPOINT}/${jobId}/`;
    const response = await makeRequest(endpoint, {
      method: 'PATCH',
      body: {
        data: {
          type: 'jobs',
          id: jobId,
          attributes: {
            askedMoreBids: true,
            fromPage: from
          }
        }
      }
    })(api);

    return normalize(response?.data, { endpoint }) as JobNormalizedEntityList;
  });

export const jobsSlice = createSlice({
  name: 'jobs',
  initialState: jobsAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {
      jobsViewState: {}
    },
    entities: {}
  } as JobSliceType
),
  reducers: {
    jobsAdded: (state, action) => {
      jobsAdapter.setMany(state, action.payload);
    },
    jobViewStateUpdate: (state, action) => {
      state.meta.jobsViewState = {
        ...state.meta.jobsViewState,
        [action.payload.jobId]: defaultsDeep(
          action.payload.changes,
          state.meta.jobsViewState[action.payload.jobId]
            ? state.meta.jobsViewState[action.payload.jobId]
            : {}
        )
      };
    },
    jobViewStateReset: (state, action) => {
      state.meta.jobsViewState = {
        ...state.meta.jobsViewState,
        [action.payload.jobId]: {}
      };
    }
  },
  extraReducers: builder => {
    // Handles the "fulfilled" action when dispatched after calling fetchJobs async action
    builder.addCase(fetchJobs.fulfilled, (state, action) => {
      const normalizedJobList = action.payload.jobs;
      // Array of ids sorted by original order
      jobsAdapter.upsertMany(state, normalizedJobList || {});
      if (action.payload.meta) {
        state.meta = {
          ...state.meta,
          ...action.payload.meta
        };
      }
      state.loading = false;
      state.error = false;
    });
    // Handles loading state, pending action is automatically sent by createAsyncThunk
    builder.addCase(fetchJobs.pending, (state, action) => {
      state.loading = true;
      state.error = false;

    });
    // Handles "rejected" state, ie failed request
    builder.addCase(fetchJobs.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });

    // Fetch a single job
    builder.addCase(fetchJob.fulfilled, (state, action) => {
      jobsAdapter.setMany(state, action.payload.jobs);
      state.loading = false;
      state.error = false;
    });
    builder.addCase(fetchJob.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(fetchJob.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });

    builder.addCase(fetchJobsStats.fulfilled, (state, action) => {
      if (action.payload) {
        state.meta = {
          ...state.meta,
          [action.payload.endpoint]: {
            ...state.meta[action.payload.endpoint],
            meta: action.payload.meta
          }
        };
      }
    });

    // Cancel a single job
    builder.addCase(cancelJob.fulfilled, (state, action) => {
      jobsAdapter.upsertMany(state, action.payload.jobs);
    });

    builder.addCase(requestExtraBid.fulfilled, (state, action) => {
      jobsAdapter.upsertMany(state, action.payload.jobs);
    });

    builder.addCase(modifyJob.fulfilled, (state, action) => {
      jobsAdapter.upsertMany(state, action.payload.jobs);
    });

    builder.addCase(postJob.fulfilled, (state, action) => {
    });
  }
});

export const jobSelectors = jobsAdapter.getSelectors((state: RootState) => state.jobs);
export const { jobViewStateUpdate, jobViewStateReset } = jobsSlice.actions;

export const selectJobMetaByStatus = createSelector(
  [
    state => state.jobs,
    (state, { status, page }: { status: JobStatus | Array<JobStatus>, page: string }) => ({ status, page })
  ],
  (jobs, { status, page }) => {
    const endpointIdentifier = getIdentifier(status);
    const jobsMeta = jobs.meta?.[`${ENDPOINT_JOB_LIST}/${endpointIdentifier}/${page}`];

    // Create a new object containing metadata and job ids
    return { ...jobsMeta, ids: jobsMeta?.data?.map(job => job.id), loading: jobs.loading };
  }
);

export default jobsSlice.reducer;
